import { useEffect } from 'react';
import { useLocalStorageValue } from '@react-hookz/web';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { useGT } from 'gt-react';
import { SystemEntity } from '@erp/shared-types';
import { orpc } from '@/api/rpc/client';
import {
  recordNavigationFromPages,
  resolveListNeighbors,
  type RecordListTarget,
} from '@/components/common/list-record-navigation';
import { useAPInvoicesList } from '@/api/rpc/hooks/accounts-payable/useAPInvoices';
import {
  INVOICES_DEFAULT_PAGE_SIZE,
  INVOICES_PAGE_SIZE_STORAGE_KEY,
  buildInvoiceListInputWithColumns,
  invoiceNavigationSearch,
} from '@/pages/authenticated/accounts-payable/invoices/invoiceListQuery';
import { useInvoiceColumnConfig } from '@/pages/authenticated/accounts-payable/invoices/invoiceColumnConfig';
import type { InvoicesSearchParams } from '@/pages/authenticated/accounts-payable/invoices/invoicesSearch';

/**
 * Previous/next for an invoice opened from the invoices table. The controls
 * show only when this invoice is on the table's current page, so a deep link
 * that is not in the filtered list stays quiet.
 */
export function useInvoiceListNavigation(invoiceId: string) {
  const search = useSearch({
    from: '/authenticated/accounts-payable/invoices',
  });
  const pageSize = useInvoicePageSize(search);
  const page = search.page && search.page > 0 ? search.page : 1;
  // Same resolved columns as the table, so the current page is one cached read.
  const columnConfig = useInvoiceColumnConfig(useGT());
  const ready = !columnConfig.isLoading;
  const listInput = (pageNumber: number) =>
    buildInvoiceListInputWithColumns(
      { ...search, page: pageNumber },
      pageSize,
      columnConfig.columns,
    );
  const current = useAPInvoicesList(listInput(page), ready);
  const edges = resolveListNeighbors({
    currentId: invoiceId,
    idOf: (invoice) => invoice.id,
    page,
    pageSize,
    total: current.pagination?.total ?? 0,
    current: { items: current.invoices, loaded: current.isSuccess },
  });
  const previous = useAPInvoicesList(
    listInput(Math.max(1, page - 1)),
    ready && edges.needsPreviousPage,
  );
  const next = useAPInvoicesList(
    listInput(page + 1),
    ready && edges.needsNextPage,
  );
  const model = recordNavigationFromPages({
    currentId: invoiceId,
    page,
    pageSize,
    current: pageSlice(current),
    previous: pageSlice(previous),
    next: pageSlice(next),
  });
  const navigate = useNavigate();
  usePrefetchInvoiceDetail(model?.previous?.id, model?.next?.id);
  if (!model) return null;

  return {
    model,
    open: (target: RecordListTarget) => {
      // No view transition: it would freeze paint and input for the whole
      // panel remount. The detail is prefetched, so the swap is one render.
      void navigate({
        to: '/accounts-payable/invoices/$invoiceId',
        params: { invoiceId: target.id },
        search: invoiceNavigationSearch(search, target.page),
        replace: true,
        resetScroll: false,
      });
    },
  };
}

/**
 * Warm the cache for both neighbors so previous/next lands on data instead
 * of a spinner. These are the two reads the detail panel needs before it
 * can paint anything: the invoice itself and its linked-entity tabs.
 */
export function usePrefetchInvoiceDetail(
  previousId: string | undefined,
  nextId: string | undefined,
) {
  const queryClient = useQueryClient();
  useEffect(() => {
    for (const id of [previousId, nextId]) {
      if (!id) continue;
      void queryClient.prefetchQuery(
        orpc.accountsPayable.invoices.get.queryOptions({ input: { id } }),
      );
      void queryClient.prefetchQuery(
        orpc.core.entityAssociations.list.queryOptions({
          input: { entityId: id, entityType: SystemEntity.AP_INVOICE },
        }),
      );
    }
  }, [previousId, nextId, queryClient]);
}

function useInvoicePageSize(search: InvoicesSearchParams): number {
  const { value: savedPageSize } = useLocalStorageValue<number>(
    INVOICES_PAGE_SIZE_STORAGE_KEY,
    { defaultValue: INVOICES_DEFAULT_PAGE_SIZE },
  );
  return search.pageSize ?? savedPageSize ?? INVOICES_DEFAULT_PAGE_SIZE;
}

type InvoiceQuery = ReturnType<typeof useAPInvoicesList>;

function pageSlice(query: InvoiceQuery) {
  return {
    items: query.invoices,
    total: query.pagination?.total ?? 0,
    isSuccess: query.isSuccess,
    isError: query.isError,
  };
}
